// ميزان المراجعة — بالتصميم الموحّد
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box, Typography, CircularProgress } from '@mui/material';
import BalanceIcon from '@mui/icons-material/Balance';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';

import accountingReportsApi from '../../api/accountingReportsApi';
import {
  ReportPage, ReportFilters, ReportSelect, ReportTableCard, useReportDateRange, fmtMoney, fmtNumber,
} from '../../components/Reports/ReportKit';
import { PrintSheet, PrintSummary, P, useReportPrint, useCompanyInfo, fmtDate } from '../../components/Reports/PrintKit';

// مسار صفحة كشف الحساب في App.jsx — عدّله إن كان مختلفًا عندك
const STATEMENT_ROUTE = '/account-statement';

const KEYS = ['openingDebit', 'openingCredit', 'transactionDebit', 'transactionCredit', 'netDebit', 'netCredit'];
const n = (v) => Number(v) || 0;
const cell = (v) => (n(v) ? fmtMoney(v) : '—');
const BORDER = '#e5e7eb';

export default function TrialBalanceReport() {
  const navigate = useNavigate();
  const range = useReportDateRange('year');
  const company = useCompanyInfo();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('nonzero');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await accountingReportsApi.getTrialBalance({ fromDate: range.dateFrom, toDate: range.dateTo });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      toast.error('فشل جلب ميزان المراجعة');
    } finally {
      setLoading(false);
    }
  }, [range.dateFrom, range.dateTo]);

  useEffect(() => {
    const id = setTimeout(fetchReport, 300);
    return () => clearTimeout(id);
  }, [fetchReport]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (view === 'nonzero' && KEYS.every(k => !n(r[k]))) return false;
      if (!q) return true;
      return String(r.accountCode ?? '').toLowerCase().includes(q) || String(r.accountName ?? '').toLowerCase().includes(q);
    });
  }, [rows, search, view]);

  // الإجماليات من كل الحسابات (لا تتأثر بالبحث) حتى يبقى التوازن صحيحًا
  const totals = useMemo(() => {
    const t = Object.fromEntries(KEYS.map(k => [k, 0]));
    rows.forEach(r => KEYS.forEach(k => { t[k] += n(r[k]); }));
    const diff = Math.round((t.netDebit - t.netCredit) * 100) / 100;
    return { ...t, diff, balanced: Math.abs(diff) < 0.01 };
  }, [rows]);

  const filteredTotals = useMemo(() => {
    const t = Object.fromEntries(KEYS.map(k => [k, 0]));
    visible.forEach(r => KEYS.forEach(k => { t[k] += n(r[k]); }));
    return t;
  }, [visible]);

  const openStatement = (r) => {
    if (!r.accountId) return;
    navigate(`${STATEMENT_ROUTE}?accountId=${r.accountId}&from=${range.dateFrom}&to=${range.dateTo}`);
  };

  const printRef = useRef(null);
  const handlePrint = useReportPrint(printRef, 'ميزان-المراجعة');
  const isFiltered = !!search.trim();

  const groupHead = (label, bg) => (
    <th colSpan={2} style={{ background: bg, textAlign: 'center' }}>{label}</th>
  );

  return (
    <ReportPage
      title="ميزان المراجعة"
      subtitle="الأرصدة الافتتاحية وحركة الفترة والأرصدة الختامية لكل الحسابات"
      icon={<BalanceIcon />}
      period={range}
      actions={[
        { label: 'تحديث', icon: <RefreshIcon />, onClick: fetchReport, disabled: loading },
        { label: 'طباعة', icon: <PrintOutlinedIcon />, onClick: handlePrint, disabled: !rows.length, primary: true },
      ]}
      stats={[
        { label: 'عدد الحسابات', value: fmtNumber(visible.length), icon: <ListAltOutlinedIcon />, color: 'slate' },
        { label: 'إجمالي الأرصدة المدينة', value: fmtMoney(totals.netDebit), icon: <TrendingUpIcon />, color: 'info' },
        { label: 'إجمالي الأرصدة الدائنة', value: fmtMoney(totals.netCredit), icon: <TrendingDownIcon />, color: 'success' },
        {
          label: totals.balanced ? 'الميزان متوازن' : 'فرق غير متوازن',
          value: totals.balanced ? '✓ 0.00' : fmtMoney(Math.abs(totals.diff)),
          icon: totals.balanced ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />,
          color: totals.balanced ? 'success' : 'error', highlight: true,
        },
      ]}
      filters={
        <ReportFilters
          range={range}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="بحث برقم الحساب أو اسمه..."
          onApply={fetchReport}
          onReset={() => { range.reset(); setSearch(''); setView('nonzero'); }}
          loading={loading}
          activeCount={[search.trim(), view !== 'nonzero'].filter(Boolean).length}
        >
          <ReportSelect
            label="الحسابات" value={view} onChange={setView} allLabel="كل الحسابات"
            options={[{ value: 'nonzero', label: 'ذات الرصيد أو الحركة' }]}
          />
        </ReportFilters>
      }
    >
      <Box sx={{ display: 'none' }}>
        <PrintSheet ref={printRef} title="ميزان المراجعة" period={`من ${fmtDate(range.dateFrom)} إلى ${fmtDate(range.dateTo)}`} company={company} landscape>
          <PrintSummary items={[
            ['الأرصدة المدينة', fmtMoney(totals.netDebit)],
            ['الأرصدة الدائنة', fmtMoney(totals.netCredit)],
            ['الفرق', fmtMoney(totals.diff)],
          ]} />
          <table style={P.table}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ ...P.th, width: 70 }}>رقم الحساب</th>
                <th rowSpan={2} style={P.th}>اسم الحساب</th>
                <th colSpan={2} style={{ ...P.th, textAlign: 'center' }}>رصيد افتتاحي</th>
                <th colSpan={2} style={{ ...P.th, textAlign: 'center' }}>حركة الفترة</th>
                <th colSpan={2} style={{ ...P.th, textAlign: 'center' }}>رصيد ختامي</th>
              </tr>
              <tr>{KEYS.map((k, i) => <th key={k} style={{ ...P.th, textAlign: 'center' }}>{i % 2 ? 'دائن' : 'مدين'}</th>)}</tr>
            </thead>
            <tbody>
              {visible.map(r => (
                <tr key={r.accountId ?? r.accountCode}>
                  <td style={P.td}>{r.accountCode}</td>
                  <td style={P.td}>{r.accountName}</td>
                  {KEYS.map(k => <td key={k} style={P.num}>{n(r[k]) ? fmtMoney(r[k]) : ''}</td>)}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2} style={{ ...P.th, ...P.total }}>{isFiltered ? 'إجمالي المعروض' : 'الإجمالي'}</td>
                {KEYS.map(k => <td key={k} style={{ ...P.num, ...P.total }}>{fmtMoney((isFiltered ? filteredTotals : totals)[k])}</td>)}
              </tr>
            </tfoot>
          </table>
        </PrintSheet>
      </Box>

      <ReportTableCard title="الحسابات" count={fmtNumber(visible.length)} height="auto">
        <Box sx={{ maxHeight: { xs: '62vh', md: '64vh' }, overflow: 'auto', position: 'relative' }}>
          {loading && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.6)', zIndex: 3 }}>
              <CircularProgress />
            </Box>
          )}
          <Box component="table" sx={{
            width: '100%', minWidth: 900, borderCollapse: 'separate', borderSpacing: 0, fontSize: 13.5,
            '& thead th': {
              position: 'sticky', zIndex: 2, bgcolor: '#f8fafc', color: '#334155', fontWeight: 800, fontSize: 13,
              px: 1.25, py: .9, borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap',
            },
            '& thead tr:first-of-type th': { top: 0 },
            '& thead tr:nth-of-type(2) th': { top: 37 },
            '& tbody td': { px: 1.25, py: .9, borderBottom: '1px solid #f1f5f9' },
            '& tbody tr': { cursor: 'pointer' },
            '& tbody tr:hover td': { bgcolor: '#f5f7ff' },
            '& .num': { direction: 'ltr', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
            '& .zero': { color: '#cbd5e1' },
            '& .sep': { borderRight: `1px solid ${BORDER}` },
            '& tfoot td': {
              position: 'sticky', bottom: 0, bgcolor: '#1e293b', color: '#fff', fontWeight: 800, px: 1.25, py: 1.1,
            },
          }}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ textAlign: 'right', width: 100 }}>رقم الحساب</th>
                <th rowSpan={2} style={{ textAlign: 'right' }}>اسم الحساب</th>
                {groupHead('رصيد افتتاحي', '#eff6ff')}
                {groupHead('حركة الفترة', '#fff7ed')}
                {groupHead('رصيد ختامي', '#ecfdf5')}
              </tr>
              <tr>
                {['#eff6ff', '#eff6ff', '#fff7ed', '#fff7ed', '#ecfdf5', '#ecfdf5'].map((bg, i) => (
                  <th key={i} style={{ background: bg, textAlign: 'center', color: i % 2 ? '#047857' : '#0369a1' }}>
                    {i % 2 ? 'دائن' : 'مدين'}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map(r => (
                  <tr key={r.accountId ?? r.accountCode} onClick={() => openStatement(r)} title="اضغط لفتح كشف الحساب">
                    <td style={{ fontWeight: 800 }}>{r.accountCode}</td>
                    <td>{r.accountName}</td>
                    {KEYS.map((k, i) => (
                      <td key={k}
                        className={`num ${n(r[k]) ? '' : 'zero'} ${i % 2 === 0 ? 'sep' : ''}`}
                        style={i >= 4 && n(r[k]) ? { fontWeight: 800, color: i === 4 ? '#0369a1' : '#047857' } : undefined}>
                        {cell(r[k])}
                      </td>
                    ))}
                  </tr>
              ))}
              {!visible.length && !loading && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', cursor: 'default' }}>لا توجد حسابات للعرض</td></tr>
              )}
            </tbody>
            {visible.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={2}>{isFiltered ? 'إجمالي المعروض' : 'الإجمالي'}</td>
                  {KEYS.map(k => <td key={k} className="num">{fmtMoney((isFiltered ? filteredTotals : totals)[k])}</td>)}
                </tr>
              </tfoot>
            )}
          </Box>
        </Box>
      </ReportTableCard>
      {!totals.balanced && rows.length > 0 && (
        <Typography sx={{ mt: 1, fontSize: 13, color: '#be123c' }}>
          الأرصدة الختامية غير متوازنة بفرق {fmtMoney(Math.abs(totals.diff))} — راجع القيود غير المتوازنة أو الحسابات المحذوفة.
        </Typography>
      )}
    </ReportPage>
  );
}
